/**
 * What the Miro import says about the chosen board before anything is imported.
 *
 * The zones import lays a template over whatever sticky notes a board holds.
 * On 2026-09-30 it read "Offsite Activities" (92 loose notes in four frames)
 * instead of "Offsite Results" (11 notes on the pyramid) and showed a tidy
 * zone count with no board name on it. These are the words that name the
 * board, count its notes, and say when no template is drawn on it.
 */

import type { MiroBoardContents } from './integrationsApi';

export interface MiroBoardNotice {
  /** Names the board and counts the sticky notes on it. */
  headline: string;
  /** Set when no template is drawn on the board, so its zones are a guess. */
  warning: string | null;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

export function miroBoardNotice(params: {
  /** The board's name as Miro lists it; absent for a pasted link Miro did not list. */
  boardName?: string | null;
  /** Sticky notes the import will bring in. */
  willImport: number;
  templateName: string;
  /** What the engine found on the board; absent from an engine that does not report it. */
  board?: MiroBoardContents | null;
}): MiroBoardNotice {
  const name = params.boardName?.trim() || 'The board you pasted';
  const onBoard = params.board?.stickyCount;

  const headline =
    onBoard === undefined
      ? `${name}: ${plural(params.willImport, 'sticky note')} will import.`
      : onBoard === params.willImport
        ? `${name}: ${plural(onBoard, 'sticky note')} on this board. All will import.`
        : `${name}: ${plural(onBoard, 'sticky note')} on this board. ${params.willImport} will import.`;

  if (!params.board || params.board.templateFound) return { headline, warning: null };

  const frames = params.board.frameTitles.length
    ? ` in frames named ${params.board.frameTitles.join(', ')}`
    : '';
  return {
    headline,
    warning:
      `No ${params.templateName} is drawn on this board. It holds only sticky notes${frames}. ` +
      'The zones below are a guess from where the notes sit. Check this is the board you meant.',
  };
}
